import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loading } from "@alm/flow-ds/components";

import CustomLink from "components/CustomLink";
import { ListGroup, ListGroupItem } from "components/ListGroup";
import PaginationButton from "components/PaginationButton";
import PageHeader from "components/PageHeader";
import usePagedData from "hooks/usePagedData";
import useCheckMountedStatus from "hooks/useCheckMountedStatus";
import addSentryBreadcrumb from "sentry/addSentryBreadcrumb";
import TaskCard from "./TaskCard";
import TasksHeading from "./TasksHeading";
import api from "lib/api";

interface Props {
	instanceId: string;
}

const TasksList = ({ instanceId }: Props) => {
	const history = useHistory();

	const { t } = useTranslation();

	const { _isMounted } = useCheckMountedStatus();

	const [init, setInit] = useState(false);

	const { state, setData, ready, addPage, loading } = usePagedData();

	/**
	 * Get menu and menu list items using id
	 *
	 * @id of instance
	 */
	const getMenuSelectors = useCallback(id => {
		return {
			menu: document.querySelector(
				`#activityInstanceMenu__dropdown__${id}`
			),
			menuList: document.querySelector(
				`#activityInstanceMenu__dropdown__${id} + div`
			),
		};
	}, []);

	/**
	 * Checks for clicks on the instance item to see if it's not the
	 * instance menu dropdown
	 *
	 * @param e event object
	 * @param id of instance
	 */
	const handleOnClick = useCallback(
		(e, id) => {
			// TODO: add this functionality back when we can actually click on tasks - need task id for that
			e.preventDefault();
			// Get menu selectors so we know which elements to ignore
			const selectors = getMenuSelectors(id);
			if (
				!selectors.menu?.contains(e.target) &&
				!selectors.menuList?.contains(e.target)
			) {
				addSentryBreadcrumb({
					category: "click",
					message: `Activity instance ${id}`,
				});
				history.push(`/admin/task/${id}?fromInstance=true`);
			}
		},
		[getMenuSelectors, history]
	);

	/**
	 * Checks if key is spacebar or enter
	 *
	 * @param e event object
	 * @param id of instance
	 */
	const handleKeyDown = useCallback(
		(e, id) => {
			// TODO: add this functionality back when we can actually click on tasks - need task id for that
			// // Get menu selectors so we know which elements to ignore
			// const selectors = getMenuSelectors(id);
			// // If key is spacebar or enter and target isn't instance dropdown then go to instance
			// if (
			// 	(e.key === "Enter" || e.keyCode === 32) &&
			// 	!selectors.menu?.contains(e.target) &&
			// 	!selectors.menuList?.contains(e.target)
			// ) {
			// 	addSentryBreadcrumb({
			// 		category: "keydown",
			// 		message: `Activity instance ${id}`,
			// 	});
			// 	e.preventDefault();
			// 	history.push(`/activity-instance/${id}`);
			// }
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[getMenuSelectors, history]
	);

	/**
	 * Load instances on mount
	 */
	useEffect(() => {
		if (!init) {
			// Load instances from API
			api.workflow
				.getActivityInstanceTasks(instanceId)
				.then(results => {
					// Only update state when the component is mounted
					if (_isMounted.current) {
						setData(results);
						ready();
						setInit(true);
					}
				})
				.catch(e => console.error(e));
		}
	}, [_isMounted, init, ready, setData, instanceId]);

	useEffect(() => {
		if (init) {
			loading();
			// Load activities from API with search query
			api.workflow
				.getActivityInstanceTasks(instanceId, {
					limit: 20,
					offset: 0,
				})
				.then(results => {
					// Only update state when the component is mounted
					if (_isMounted.current) {
						setData(results);
						ready();
					}
				})
				.catch(e => console.error(e));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const page = useCallback(() => {
		addSentryBreadcrumb({
			category: "button.click",
			message: "Instances page",
		});

		loading();
		api.workflow
			.getActivityInstanceTasks(instanceId, {
				limit: state.limit,
				offset: state.offset,
			})
			.then(res => {
				addPage(res);
				ready();
			})
			.catch(e => console.error(e));
	}, [addPage, loading, ready, state.limit, state.offset, instanceId]);
	if (!init) return <Loading className="mt-4 elem__loader--md" />;

	return (
		<>
			<PageHeader title={t("main.activityInstances.headings.tasks")} />
			{state.results.length ? (
				<>
					<TasksHeading />
					<ListGroup className="list-group-links">
						{state.results.map((task, index: number) => (
							<ListGroupItem
								className={"activity-instances-card--container"}
								key={`activity-instance__${index}`}>
								{/* CustomLink checks click and keydown didn't come from the dropdown menu */}
								<CustomLink
									handleClick={(e: React.MouseEvent) =>
										handleOnClick(e, task.id)
									}
									handleKeyDown={(e: React.KeyboardEvent) =>
										handleKeyDown(e, task.title)
									}
									label={task.title}
									to={`/admin/task/${task.id}`}
									className="activity-instances-card__task">
									<TaskCard {...task} />
								</CustomLink>
							</ListGroupItem>
						))}
					</ListGroup>
				</>
			) : (
				!state.loading && (
					<p className="no-instances">
						{t("main.activityInstances.text.noInstances")}
					</p>
				)
			)}
			{state.hasMore ? (
				<PaginationButton
					pageType="activityInstanceTasks"
					loading={state.loading}
					page={page}
					hasMore={state.hasMore}
				/>
			) : (
				<div className="mb-50"></div>
			)}
		</>
	);
};

export default TasksList;
